import type { StatusTone } from "@/components/ui";
import type {
  AccountAction,
  UserAccountStatus,
  VerificationStatus,
} from "./types";

export function accountStatusTone(status: UserAccountStatus): StatusTone {
  switch (status) {
    case "active":
      return "success";
    case "created":
      return "info";
    case "restricted":
      return "warning";
    case "suspended":
    case "banned":
      return "danger";
    case "closed":
      return "neutral";
    default:
      return "neutral";
  }
}

export function verificationStatusTone(status: VerificationStatus): StatusTone {
  switch (status) {
    case "verified":
    case "accepted":
      return "success";
    case "requested":
    case "submitted":
    case "under_review":
    case "more_information_required":
      return "warning";
    case "rejected":
    case "revoked":
    case "expired":
      return "danger";
    default:
      return "neutral";
  }
}

export function formatLabel(value: string): string {
  return value.replaceAll("_", " ");
}

/**
 * Play's membership tiers (HUDDL_Play lib/account.ts TIERS). Names only;
 * prices are Play's. Tiers 2 and 3 (Partner, Ambassador) read as Verified
 * until they launch as new tiers after launch (Play Polishing 18.2).
 */
const TIER_NAMES = ["User", "Verified", "Verified", "Verified"] as const;

export function tierName(tier: number): string {
  return TIER_NAMES[tier] ?? `Tier ${tier}`;
}

/** "2026-09-28T19:02:16Z" -> "28 Sep 2026, 7:02 pm" in the operator's locale. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { dateStyle: "medium" });
}

/** "3 days ago" style, for last-seen columns. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.floor(hr / 24);
  if (day < 60) return `${day} d ago`;
  return formatDate(iso);
}

export function bookingStatusTone(status: string): StatusTone {
  switch (status) {
    case "confirmed":
    case "in_progress":
    case "completed":
      return "success";
    case "pending_confirmation":
    case "pending_payment":
    case "initiated":
      return "warning";
    case "cancelled":
    case "failed":
    case "disputed":
      return "danger";
    default:
      return "neutral";
  }
}

/** The account actions that are legal from a status (mirrors backend lifecycle.ts). */
export function legalAccountActions(status: UserAccountStatus): AccountAction[] {
  switch (status) {
    // Warnings, restrictions, suspensions and bans are issued from a report in
    // Trust and Safety (the i106 ladder), never by changing status here.
    case "created":
      return ["activate", "close"];
    case "active":
      return ["close"];
    case "restricted":
      return ["restore", "close"];
    case "suspended":
      return ["restore", "close"];
    case "banned":
      return ["restore", "close"];
    default:
      return [];
  }
}

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}
